/**
 *
 * @module mediaManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/playback-session.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/playhead.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md
 *
 */

import Check from 'check-types';
import EventEmitter from 'events';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Drm from './../drm/entry';
import Token from './../token/entry';
import Internal from './../internal/entry';
import MediaItem from './mediaItem';
import MediaCapabilitiesProvider from './mediaCapabilitiesProvider';
import MediaDescriptor from './mediaDescriptor';
import PlayerAdapter from './playerAdapter';
import AdInsertionStrategy from './adInsertionStrategy';
import PlaybackTelemetryDispatcher from './playbackTelemetryDispatcher';
import PlaybackTelemetryConfiguration from './playbackTelemetryConfiguration';
import PlaybackSession from './playbackSession';
import AuthCookieProvider from './authCookieProvider';
import AdEngineManager from './adEngine/adEngineManager';
import HdrType from './hdrType';

const Util = Services.Util;
const DrmManager = Drm.DrmManager;
const TokenManager = Token.TokenManager;
const TelemetryManager = Internal.Telemetry.TelemetryManager;
const AccessToken = Services.Token.AccessToken;
const MediaClient = Services.Media.MediaClient;
const MediaManagerConfiguration = Services.Configuration.MediaManagerConfiguration;
const Presentation = Services.Media.Presentation;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    instanceStrict,
    nonEmptyObject,
    nonEmptyString,
    number
} = Check.assert;

/**
 *
 * @class MediaManager
 * @extends {EventEmitter}
 * @see https://nodejs.org/api/events.html
 * @desc Manages a media player to help load, control, and monitor playback on your behalf.
 * @throws {InvalidArgumentException}
 *
 */
export default class MediaManager extends EventEmitter {

    /**
     *
     * @param {Object} options
     * @param {SDK.Services.Configuration.MediaManagerConfiguration} options.mediaManagerConfiguration
     * @param {SDK.Token.TokenManager} options.tokenManager
     * @param {SDK.Drm.DrmManager} options.drmManager
     * @param {SDK.Internal.Telemetry.TelemeteryManager} options.telemetryManager
     * @param {SDK.Media.MediaCapabilitiesProvider} [options.mediaCapabilitiesProvider]
     * @param {SDK.Services.Media.MediaClient} options.mediaClient
     * @param {SDK.Media.AdEngine.AdEngineManager} options.adEngineManager
     * @param {SDK.Logging.Logger} [options.logger=Logging]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'MediaManager(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get mediaManagerConfiguration() {
                return `${this.ctor} needs SDK.Services.Configuration.MediaManagerConfiguration instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            },
            get drmManager() {
                return `${this.ctor} needs SDK.Drm.DrmManager instance`;
            },
            get telemetryManager() {
                return `${this.ctor} needs SDK.Internal.TelemetryManager instance`;
            },
            get mediaCapabilitiesProvider() {
                return `${this.ctor} needs SDK.Media.MediaCapabilitiesProvider instance`;
            },
            get mediaClient() {
                return `${this.ctor} needs MediaClient instance`;
            },
            get adEngineManager() {
                return `${this.ctor} needs SDK.Media.AdEngine.AdEngineManager instance`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { mediaManagerConfiguration, tokenManager, drmManager, telemetryManager } = options;
        const { mediaCapabilitiesProvider, mediaClient, adEngineManager, logger = Logging } = options;

        instanceStrict(mediaManagerConfiguration, MediaManagerConfiguration, errorMessage.mediaManagerConfiguration, InvalidArgumentException); // eslint-disable-line
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);
        instanceStrict(drmManager, DrmManager, errorMessage.drmManager, InvalidArgumentException);
        instanceStrict(telemetryManager, TelemetryManager, errorMessage.telemetryManager, InvalidArgumentException);

        if (Check.assigned(mediaCapabilitiesProvider)) {
            instanceStrict(
                mediaCapabilitiesProvider,
                MediaCapabilitiesProvider,
                errorMessage.mediaCapabilitiesProvider,
                InvalidArgumentException
            );
        }

        instanceStrict(mediaClient, MediaClient, errorMessage.mediaClient, InvalidArgumentException);
        instanceStrict(adEngineManager, AdEngineManager, errorMessage.adEngineManager, InvalidArgumentException);

        super();

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.MediaManagerConfiguration}
         *
         */
        this.config = mediaManagerConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Drm.DrmManager}
         *
         */
        this.drmManager = drmManager;

        /**
         *
         * @access private
         * @type {SDK.Internal.Telemetry.TelemetryManager}
         *
         */
        this.telemetryManager = telemetryManager;

        /**
         *
         * @access private
         * @type {SDK.Media.AdEngine.AdEngineManager}
         *
         */
        this.adEngineManager = adEngineManager;

        /**
         *
         * @access private
         * @type {SDK.Media.MediaCapabilitiesProvider}
         *
         */
        this.mediaCapabilitiesProvider = mediaCapabilitiesProvider || new MediaCapabilitiesProvider(logger);

        /**
         *
         * @access private
         * @type {SDK.Services.Media.MediaClient}
         *
         */
        this.client = mediaClient;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        this.logger.log(MediaManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {MediaDescriptor} mediaDescriptor
     * @desc Retrieves a media item, based on the descriptor.
     * @returns {Promise<SDK.Media.MediaItem>}
     *
     */
    getMediaItem(mediaDescriptor) {

        const errorMessage = {
            method: `${this.constructor.name}.getMediaItem(mediaDescriptor)`,
            get mediaDescriptor() {
                return `${this.method} needs MediaDescriptor instance`;
            }
        };

        instanceStrict(mediaDescriptor, MediaDescriptor, errorMessage.mediaDescriptor, InvalidArgumentException);

        const { accessToken, client } = this;
        const { mediaLocation, adInsertionStrategy, basePlaybackScenario } = mediaDescriptor;
        const playbackScenario = this.getPlaybackScenario(basePlaybackScenario, adInsertionStrategy);

        return client.getMediaPayload(mediaLocation, accessToken, playbackScenario).then((mediaPayload) => {
            return Promise.resolve(new MediaItem(mediaPayload, mediaDescriptor));
        });

    }

    /**
     *
     * @access public
     * @param {PlayerAdapter} playerAdapter - The player adapter you would like to create a new media session for.
     * @param {Array<SDK.Services.Media.DrmType>} [priorityDrms=null] - override drm with a specified drm or order
     * of Drms
     * @desc Creates a playback session (A contiguous block of time while a user is consuming a hosted media item).
     * @returns {Promise<PlaybackSession>} Injected with all dependencies.
     * @example mediaManager.createPlaybackSession(playerAdapter).then((playbackSession) => {});
     *
     */
    createPlaybackSession(playerAdapter, priorityDrms = null) {

        const errorMessage = {
            method: `${this.constructor.name}.createPlaybackSession(playerAdapter)`,
            get playerAdapter() {
                return `${this.method} needs PlayerAdapter instance`;
            },
            get priorityDrms() {
                return `${this.method} needs an array of SDK.Service.Media.DrmType`;
            }
        };

        instanceStrict(playerAdapter, PlayerAdapter, errorMessage.playerAdapter, InvalidArgumentException);

        if (Check.assigned(priorityDrms)) {
            array.of.nonEmptyString(priorityDrms, errorMessage.priorityDrms, InvalidArgumentException);
        }

        const { config, drmManager, tokenManager, telemetryManager, adEngineManager, logger } = this;

        const mediaManager = this;
        const authCookieProvider = new AuthCookieProvider(mediaManager);

        const playbackTelemetryConfiguration = new PlaybackTelemetryConfiguration(
            config.extras.playbackSession.streamSampleInterval
        );
        const playbackTelemetryDispatcher = new PlaybackTelemetryDispatcher(
            playbackTelemetryConfiguration,
            telemetryManager,
            logger
        );

        logger.info(MediaManager.name, 'Create playback session.');

        const options = {
            tokenManager,
            drmManager,
            mediaManager,
            telemetryDispatcher: playbackTelemetryDispatcher,
            logger,
            playerAdapter,
            authCookieProvider,
            adEngineManager,
            priorityDrms
        };

        return Promise.resolve(new PlaybackSession(options));

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Token.AccessToken} accessToken - The current access token.
     * @desc Creates an auth cookie
     * @returns {Promise<Void>}
     *
     */
    createAuthCookie(accessToken) {

        const errorMessage = {
            method: `${this.constructor.name}.createAuthCookie(accessToken)`,
            get accessToken() {
                return `${this.method} needs AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { client } = this;

        return client.createAuthCookie(accessToken);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Get the playback scenario mapping for the media client.
     * @param {String} [playbackScenario] - playback scenario override provided by the application developer
     * @param {AdInsertionStrategy} [adInsertionStrategy] - The advertising insertion strategy to be used with the media
     * @returns {String}
     *
     */
    getPlaybackScenario(playbackScenario, adInsertionStrategy) {

        const { playbackScenarioDefault } = this.config.extras;
        const { mediaCapabilitiesProvider } = this;
        const scenario = Check.nonEmptyString(playbackScenario) ? playbackScenario : playbackScenarioDefault;

        let strategy;
        let hdrType;

        const mediaCapabilities = mediaCapabilitiesProvider.getSupportedHdrTypes();

        if (mediaCapabilities.includes(HdrType.dolbyVision)) {
            hdrType = '-dovi';
        } else if (mediaCapabilities.includes(HdrType.hdr10)) {
            hdrType = '-hdr10';
        }

        const derivedScenario = Check.nonEmptyString(hdrType) ? `${scenario}${hdrType}` : scenario;

        if (adInsertionStrategy === AdInsertionStrategy.CSAI) {
            strategy = '~csai';
        }

        if (adInsertionStrategy === AdInsertionStrategy.SSAI) {
            strategy = '~ssai';
        }

        return Check.nonEmptyString(strategy) ? `${derivedScenario}${strategy}` : derivedScenario;

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Media.MediaItem} mediaItem - The MediaItem for the current media that was returned by a call to
     * Fetch.
     * @desc Fetches the details about the spritesheet thumbnail images that players can show to users when scrubbing
     * video.
     * @returns {Promise<Array<SDK.Services.Media.SpriteThumbnailSet>>}
     *
     */
    getSpriteSheetThumbnailSets(mediaItem) {

        const errorMessage = {
            method: `${this.constructor.name}.getSpriteSheetThumbnailSets(mediaItem)`,
            get mediaItem() {
                return `${this.method} mediaItem should be an instance of SDK.Media.MediaItem`;
            }
        };

        instanceStrict(mediaItem, MediaItem, errorMessage.mediaItem, InvalidArgumentException);

        const { accessToken, client } = this;

        const thumbnailLink = Util.getSafe(() => mediaItem.payload.thumbnails.spritesheet);

        const options = {
            thumbnailLink,
            accessToken
        };

        return client.getSpriteSheetThumbnails(options);

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Media.MediaItem} mediaItem - The MediaItem for the current media that was returned by a call to
     * Fetch.
     * @param {String} resolution - The desired resolution of thumbnails to fetch
     * @desc Fetches the details about the spritesheet thumbnail images that players can show to users when scrubbing
     * video.
     * @returns {Promise<SDK.Services.Media.SpriteThumbnailSet>}
     *
     */
    getSpriteSheetThumbnailSet(mediaItem, resolution) {

        const errorMessage = {
            method: `${this.constructor.name}.getSpriteSheetThumbnailSets(mediaItem, resolution)`,
            get mediaItem() {
                return `${this.method} mediaItem should be an instance of SDK.Media.MediaItem`;
            },
            get resolution() {
                return `${this.method} resolution should be a String with length`;
            }
        };

        instanceStrict(mediaItem, MediaItem, errorMessage.mediaItem, InvalidArgumentException);
        nonEmptyString(resolution, errorMessage.resolution, InvalidArgumentException);

        const { accessToken, client } = this;

        const thumbnailLink = Util.getSafe(() => mediaItem.payload.thumbnails.spritesheet);

        const options = {
            thumbnailLink,
            resolution,
            accessToken
        };

        return client.getSpriteSheetThumbnails(options).then((thumbnailSets) => {
            return Promise.resolve(thumbnailSets[0]);
        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Services.Media.Presentation} presentation - The thumbnail presentation to download. Typically chosen
     * by desired resolution.
     * @param {Number} index - The index of the thumbnail in the pod to download.
     * @desc Fetches the details about the spritesheet thumbnail images that players can show to users when scrubbing
     * video.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getSpriteSheetThumbnail(presentation, index) {

        const errorMessage = {
            method: `${this.constructor.name}.getSpriteSheetThumbnail(presentation, index)`,
            get presentation() {
                return `${this.method} presentation should be an instance of SDK.Media.Presentation`;
            },
            get index() {
                return `${this.method} index should be a Number`;
            }
        };

        instanceStrict(presentation, Presentation, errorMessage.presentation, InvalidArgumentException);
        number(index, errorMessage.index, InvalidArgumentException);

        const { client } = this;

        return client.getSpriteSheetThumbnail(presentation, index);

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Media.MediaItem} mediaItem - The MediaItem for the current media that was returned by a call to
     * Fetch.
     * @desc Fetches the details about the BIF thumbnail images that players can show to users when scrubbing video.
     * @returns {Promise<Array<SDK.Services.Media.BifThumbnailSet>>}
     *
     */
    getBifThumbnailSets(mediaItem) {

        const errorMessage = {
            method: `${this.constructor.name}.getBifThumbnailSets(mediaItem)`,
            get mediaItem() {
                return `${this.method} mediaItem should be an instance of SDK.Media.MediaItem`;
            }
        };

        instanceStrict(mediaItem, MediaItem, errorMessage.mediaItem, InvalidArgumentException);

        const { accessToken, client } = this;

        const thumbnailLink = Util.getSafe(() => mediaItem.payload.thumbnails.bif);

        const options = {
            thumbnailLink,
            accessToken
        };

        return client.getBifThumbnails(options);

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @param {SDK.Media.MediaItem} mediaItem - The MediaItem for the current media that was returned by a call to
     * Fetch.
     * @param {SDK.Media.ThumbnailResolution} resolution - The desired resolution of thumbnails to fetch.
     * @desc Fetches the details about the BIF thumbnail images that players can show to users when scrubbing video.
     * @returns {Promise<SDK.Services.Media.BifThumbnailSet>}
     *
     */
    getBifThumbnailSet(mediaItem, resolution) {

        const errorMessage = {
            method: `${this.constructor.name}.getBifThumbnailSet(mediaItem, resolution)`,
            get mediaItem() {
                return `${this.method} mediaItem should be an instance of SDK.Media.MediaItem`;
            },
            get resolution() {
                return `${this.method} resolution should be a String with length`;
            }
        };

        instanceStrict(mediaItem, MediaItem, errorMessage.mediaItem, InvalidArgumentException);
        nonEmptyString(resolution, errorMessage.resolution, InvalidArgumentException);

        const { accessToken, client } = this;

        const thumbnailLink = Util.getSafe(() => mediaItem.payload.thumbnails.bif);

        const options = {
            thumbnailLink,
            resolution,
            accessToken
        };

        return client.getBifThumbnails(options).then((thumbnailSets) => {
            return Promise.resolve(thumbnailSets[0]);
        });

    }

    /**
     *
     * @access private
     * @since 3.8.0
     * @note the JS SDK does not support this method
     * @returns {Promise<Void>}
     *
     */
    getBifThumbnail() {

        const errorMessage = {
            method: `${this.constructor.name}.getBifThumbnail()`
        };

        return new Promise((resolve, reject) => {
            return reject(new Error(`${errorMessage.method} - not-implemented`));
        });

    }

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.tokenManager.getAccessToken();

    }

}



// WEBPACK FOOTER //
// ./src/media/mediaManager.js